<template #default>
  <div class="page-view">
    <a-card class="page-filter" :bordered="false">
      <a-form
        layout="horizontal"
        :label-col="labelCol"
        :wrapper-col="wrapperCol"
        @submit.prevent="onSearch"
      >
        <a-row :gutter="16" align="middle">
          <a-col :span="12">
            <a-form-item label="InstClass">
              <a-tree-select
                v-model:value="query.externalInstClass"
                :dropdown-style="{ maxHeight: '400px', overflow: 'auto' }"
                style="width: 100%"
                tree-default-expand-all
                placeholder="Please select"
                :show-search="true"
                :tree-data="instClassOptions"
                tree-node-filter-prop="label"
              >
                <template #title="{ label }"> {{ label }}</template>
              </a-tree-select>
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="Exchange">
              <a-select
                v-model:value="query.exchangeCode"
                style="width: 100%"
                allow-clear
                placeholder="All"
                :show-search="true"
                :filter-option="filterOption"
              >
                <a-select-option
                  v-for="item in exchangeList"
                  :key="item.exchName"
                  :value="item.exchName"
                >
                  {{ item.exchName }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
        </a-row>

        <a-row :gutter="16" align="middle">
          <a-col :span="12">
            <a-form-item label="Name">
              <a-input
                v-model:value="query.displayCode"
                @pressEnter="onSearch"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="Description">
              <a-input
                v-model:value="query.securityDesc"
                @pressEnter="onSearch"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
        </a-row>

        <a-row :gutter="16" align="middle">
          <a-col :span="12">
            <a-form-item label="Sec Id">
              <a-input
                v-model:value="query.secId"
                @pressEnter="onSearch"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
        </a-row>

        <a-row :gutter="16" align="middle">
          <a-col :span="12">
            <a-form-item label="Identifier Type">
              <a-select
                v-model:value="query.identType"
                allow-clear
                :show-search="true"
                :filter-option="filterOption"
              >
                <a-select-option
                  v-for="item in identifierTypeList"
                  :key="item"
                  :value="item"
                >
                  {{ item }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="Identifier">
              <a-auto-complete
                v-model:value="query.identifier"
                :options="tickerOptions"
                @search="onSearchTicker"
                @select="onSearchTicker"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
        </a-row>

        <a-row :gutter="16" align="middle">
          <a-col :span="12">
            <a-form-item
              :colon="false"
              :labelCol="{ span: 6, style: { textAlign: 'left' } }"
              :wrapperCol="{ span: 18 }"
            >
              <div class="page-actions">
                <a-button type="primary" @click="onSearch" :loading="loading"
                  >Search</a-button
                >
                <a-button @click="showAddEqtyModal">
                  <PlusOutlined />
                  Add Eqty
                </a-button>
                <a-button @click="showAddBondModal">
                  <PlusOutlined />
                  Add Bond
                </a-button>
                <a-button @click="showCopyModal">Copy</a-button>
              </div>
            </a-form-item>
          </a-col>
        </a-row>
      </a-form>
    </a-card>

    <div class="page-grid page-grid--h540">
      <AgGridVue
        style="width: 100%; height: 100%"
        :theme="themeBalham"
        :loading="loading"
        :gridOptions="gridOptions"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :rowData="rowData"
      />
    </div>
    <AddEditStockView
      v-model:visible="addStockModalVisible"
      :isEdit="isEdit"
      :addForm="addForm"
      @update:security="handleUpdateSecurity"
    />
    <AddEditBondView
      v-model:visible="addBondModalVisible"
      :isEdit="isEdit"
      :addForm="addForm"
      @update:security="handleUpdateSecurity"
    />
    <IdentifierView
      v-model:visible="identifierModalVisible"
      :rawData="identifierRawData"
      @update:identifiers="handleUpdateIdentifiers"
    />
  </div>
</template>

<script setup>
import { ref, defineExpose, onMounted, provide } from 'vue'
// import { useStore } from 'vuex'
import { themeBalham } from 'ag-grid-community'

import { message } from 'ant-design-vue'
import { PlusOutlined } from '@ant-design/icons-vue'

import stockBondView_ColumnDefs from '@/views/security/stockbond/StockBondView_ColumnDefs'
import AgEditCellRenderer from '@/components/AgEditCellRenderer.vue'
import AddEditStockView from './AddEditStockView.vue'
import AddEditBondView from './AddEditBondView.vue'
import IdentifierView from '../IdentifierView.vue'

import reference from '@/api/reference'
import security from '@/api/security'
import dayjs from 'dayjs'
import { mapIdentifierField } from '@/utils/secutils'
import debounce from 'lodash/debounce'

// const store = useStore()
const gridApi = ref(null)
const gridOptions = ref({
  rowSelection: 'single',

  onGridReady: (params) => {
    gridApi.value = params.api
  },
})

const defaultColDef = {
  filter: true,
  sortable: true,
  resizable: true,
}

let columnDefs = stockBondView_ColumnDefs

defineExpose({
  AgEditCellRenderer,
})

const labelCol = {
  span: 6,
  style: { textAlign: 'left' },
}

const wrapperCol = {
  span: 18,
}

const loading = ref(false)
const rowData = ref([])
const instClassOptions = ref([])
const eqtyInstClassList = ref([])
const bondInstClassList = ref([])
const exchangeList = ref([])
const identifierTypeList = ref([])
const tickerOptions = ref([])
const tickerList = ref([])
const issuerList = ref([])
const identifierRawData = ref({
  securityId: null,
  identifiers: [],
})
const addStockModalVisible = ref(false)
const addBondModalVisible = ref(false)
const addForm = ref({})
const isEdit = ref(false)
const identifierModalVisible = ref(false)

const query = ref({
  assetClass: 'EQTY',
  externalInstClass: 'EQTY',
  exchangeCode: null,
  displayCode: null,
  securityDesc: null,
  secId: null,
  identType: null,
  identifier: null,
  effectiveDate: null,
})

const searchTicker = (ticker) => {
  if (!ticker || ticker.length < 3 || !query.value.identType) {
    return
  }
  const identType = query.value.identType
  security
    .getSimpleSecurities({
      tickerOperator: 'START_WITH',
      identType: identType,
      identifier: ticker,
    })
    .then((res) => {
      tickerList.value = res.data
      const tickers = res.data
        .filter((item) => {
          return mapIdentifierField(item, identType)
        })
        .map((item) => {
          return mapIdentifierField(item, identType)
        })
      tickers.sort((a, b) => b.localeCompare(a))
      const arr = tickers.length > 8 ? tickers.slice(0, 8) : tickers
      tickerOptions.value = arr.map((item) => ({ value: item }))
    })
}
const onSearchTicker = debounce(
  function (ticker) {
    searchTicker(ticker)
  },
  600,
  { maxWait: 1000 }
)

const onSearch = async () => {
  loading.value = true
  try {
    const params = { ...query.value }
    const allOtherFieldsEmpty =
      !params.exchangeCode &&
      (!params.displayCode || !params.displayCode.trim()) &&
      (!params.securityDesc || !params.securityDesc.trim()) &&
      !params.secId &&
      (!params.identifier || !params.identifier.trim())

    if (
      allOtherFieldsEmpty &&
      (!params.externalInstClass ||
        params.externalInstClass === 'EQTY' ||
        params.externalInstClass === 'FIXED_INCOME')
    ) {
      return
    }

    if (
      !params.secId &&
      (!params.identifier || !params.identifier.trim()) &&
      !params.externalInstClass
    ) {
      message.warning('Please select instrument class')
      return
    }

    if (
      params.externalInstClass === 'EQTY' ||
      params.externalInstClass === 'FIXED_INCOME'
    ) {
      params.assetClass = params.externalInstClass
      params.externalInstClass = null
    } else {
      params.assetClass = null
    }

    if (!params.identifier || !params.identifier.trim()) {
      params.identType = null
    }
    params.tickerOperator = 'START_WITH'
    const resp = await security.getSimpleSecurities(params)
    const data = resp.data
    rowData.value = Array.isArray(data) ? data : []
    if (rowData.value.length === 0) {
      message.info('No data found.')
    }
  } catch (e) {
    message.error(e.response.data.message || 'Query failed')
  } finally {
    loading.value = false
  }
}

const showAddEqtyModal = () => {
  showAddModal('EQTY')
}

const showAddBondModal = () => {
  showAddModal('FIXED_INCOME')
}

const showAddModal = (assetClass) => {
  isEdit.value = false
  addForm.value = {
    securityId: null,
    externalSecurityId: null,
    assetClass: assetClass,
    issueDate: null,
    settleDate: null,
    maturityDate: null,
    defaultDate: null,
    exchange: {
      exchangeId: null,
      exchName: null,
      micExchName: null,
    },
    issuer: {
      issuerId: null,
      shortName: null,
      name: null,
    },
    underlyingTicker: null,
    instrumentClass: null,
    isin: null,
    ticker: null,
    name: null,
    desc: null,
    priceMultiplier: 1,
    contractSize: 1,
    tickSize: 1,
    tickValue: 1,
    pnlCurrency: null,
    isActive: true,
  }
  if (assetClass === 'EQTY') {
    addStockModalVisible.value = true
    addBondModalVisible.value = false
  } else if (assetClass === 'FIXED_INCOME') {
    addBondModalVisible.value = true
    addStockModalVisible.value = false
  }
}

const showCopyModal = () => {
  const selectedRows = gridApi.value.getSelectedRows()
  if (selectedRows.length !== 1) {
    message.warning('Please select one row to copy')
    return
  }

  const params = { securityId: selectedRows[0].securityId }
  security
    .getSecurity(params)
    .then((resp) => {
      if (resp.data) {
        const data = resp.data
        addForm.value = {
          ...data,
          securityId: undefined,
          externalSecurityId: undefined,
          exchange: { ...data.exchange },
          issuer: { ...data.issuer },
          identifiers: data.identifiers ? [...data.identifiers] : [],
          underlying: data.underlying ? { ...data.underlying } : null,
        }
        addForm.value = formatDateTime(addForm.value)
        isEdit.value = false
        if (addForm.value.assetClass === 'EQTY') {
          addStockModalVisible.value = true
          addBondModalVisible.value = false
        } else if (addForm.value.assetClass === 'FIXED_INCOME') {
          addBondModalVisible.value = true
          addStockModalVisible.value = false
        }
      }
    })
    .catch((error) => {
      message.error(
        error.response.data.message || 'Failed to fetch security details'
      )
    })
}
const formatDateTime = (security) => {
  const formatDateTime = (val) =>
    val ? dayjs(dayjs(val).format('YYYY-MM-DD')) : null

  addForm.value.issueDate = formatDateTime(security.issueDate)
  security.ipoDate = formatDateTime(security.ipoDate)
  security.settleDate = formatDateTime(security.settleDate)
  security.maturityDate = formatDateTime(security.maturityDate)
  security.firstTradeDate = formatDateTime(security.firstTradeDate)
  security.lastTradeDate = formatDateTime(security.lastTradeDate)
  security.firstNoticeDate = formatDateTime(security.firstNoticeDate)
  security.firstExerciseDate = formatDateTime(security.firstExerciseDate)
  security.accrualDate = formatDateTime(security.accrualDate)
  security.firstCouponDate = formatDateTime(security.firstCouponDate)
  security.defaultDate = formatDateTime(security.defaultDate)

  return security
}

const showEditModal = (event) => {
  const data = event?.data || event
  const params = { securityId: data.securityId }
  security
    .getSecurity(params)
    .then((resp) => {
      if (resp.data) {
        addForm.value = resp.data
        addForm.value = formatDateTime(addForm.value)
        if (addForm.value.priceMultiplier) {
          addForm.value.priceDivisor = 1 / addForm.value.priceMultiplier
        }
        isEdit.value = true
        if (addForm.value.assetClass === 'EQTY') {
          addStockModalVisible.value = true
          addBondModalVisible.value = false
        } else if (addForm.value.assetClass === 'FIXED_INCOME') {
          addBondModalVisible.value = true
          addStockModalVisible.value = false
        }
      }
    })
    .catch((error) => {
      message.error(
        error.response.data.message || 'Failed to fetch security details'
      )
    })
}

const handleUpdateSecurity = (newSecurity, isEdit) => {
  loading.value = true
  if (isEdit) {
    security
      .updateSec(newSecurity)
      .then(() => {
        message.success('Security updated successfully')
      })
      .catch((error) => {
        message.error(
          error.response.data.message || 'Failed to update security'
        )
      })
      .finally(() => {
        loading.value = false
      })
  } else {
    security
      .createSec(newSecurity)
      .then(() => {
        message.success('Security created successfully')
      })
      .catch((error) => {
        message.error(
          error.response.data.message || 'Failed to create security'
        )
      })
      .finally(() => {
        loading.value = false
      })
  }
}

const filterOption = (input, option) => {
  return option.key.toLowerCase().indexOf(input.toLowerCase()) >= 0
}

const showIdentifierModal = (event) => {
  const data = event?.data || event
  const params = { securityId: data.securityId }
  security
    .getSecurity(params)
    .then((resp) => {
      identifierRawData.value.securityId = data.securityId
      identifierRawData.value.identifiers = resp.data.identifiers || []
      identifierModalVisible.value = true
    })
    .catch((error) => {
      message.error(
        error.response.data.message || 'Failed to fetch security details'
      )
    })
}

columnDefs = columnDefs.map((col) => {
  if (col.field === 'identifiers') {
    return {
      ...col,
      cellRendererParams: {
        editButton: 'Identifiers',
        showEditModal: (data) => showIdentifierModal(data),
      },
    }
  }
  if (col.field === 'edit') {
    return {
      ...col,
      cellRendererParams: {
        editButton: 'Edit',
        showEditModal: (data) => showEditModal(data),
      },
    }
  }
  return col
})

const handleUpdateIdentifiers = () => {
  /* loading.value = true
  security
    .updateIdentifiers(securityId, newIdentifiers)
    .then(() => {
      message.success('Identifiers updated successfully')
    })
    .catch((error) => {
      console.error('Error updating identifiers:', error)
    })
    .finally(() => {
      loading.value = false
    }) */
}

onMounted(async () => {
  try {
    const result = await reference.getReferenceDataV2()
    eqtyInstClassList.value = result.data?.eqtyTypes || []
    eqtyInstClassList.value.push('WRNT')
    bondInstClassList.value = result.data?.bondTypes || []
    instClassOptions.value = [
      {
        label: 'EQTY',
        value: 'EQTY',
        // disabled: true,
        // selectable: false,
        children: eqtyInstClassList.value
          .filter((item) => item !== 'SWAP')
          .map((item) => ({
            label: item,
            value: item,
          })),
      },
      {
        label: 'FIXED_INCOME',
        value: 'FIXED_INCOME',
        children: bondInstClassList.value.map((item) => ({
          label: item,
          value: item,
        })),
      },
    ]
    exchangeList.value = result.data?.exchanges || []
    identifierTypeList.value = result.data?.identifierTypes || []
    issuerList.value = result.data?.issuers || []
  } catch (e) {
    console.error('Failed to fetch reference lists:', e)
  }
})

provide('exchangeList', exchangeList)
provide('issuerList', issuerList)
provide('eqtyInstClassList', eqtyInstClassList)
provide('bondInstClassList', bondInstClassList)
provide('identifierTypeList', identifierTypeList)
provide('security', security)
</script>
